import { Genql } from '@bizzdesign/bizzdesign-sdk-type-genql';
import { assertIsUUID, type UUID } from '@bizzdesign/sdk-internal/uuid';
import type {
  Comment,
  CommentCreatedPayload,
  CommentEvent,
  CommentReaction,
  CommentReactionPayload,
  CommentSystemAttributes,
  CommentThread,
  CommentThreadDeletedPayload,
  CommentThreadResolvedPayload,
  CommentThreadSort,
  CommentThreadUpdatedPayload,
  CommentUser,
  CommentsDeletedPayload,
  CommentUpdatedPayload,
} from './interfaces';

type GenqlCommentUserLike = Pick<
  Genql.UserInfo,
  'id' | 'username' | 'firstName' | 'lastName' | 'email'
>;

type GenqlCommentUserIdLike = Pick<Genql.UserInfo, 'id'>;

type GenqlSystemAttributesLike = Pick<
  Genql.SystemAttributes,
  'createdAt' | 'updatedAt' | 'hiddenAt' | 'isHidden'
> & {
  createdBy: GenqlCommentUserLike | null;
  updatedBy: GenqlCommentUserLike | null;
  hiddenBy: GenqlCommentUserLike | null;
};

type GenqlCommentReactionLike = Pick<Genql.CommentReaction, 'emoji'> & {
  users: GenqlCommentUserLike[];
};

export type GenqlCommentLike = Pick<
  Genql.Comment,
  'id' | 'threadId' | 'text'
> & {
  author: GenqlCommentUserLike;
  reactions: GenqlCommentReactionLike[];
  readBy: GenqlCommentUserIdLike[] | null;
  systemAttributes: GenqlSystemAttributesLike;
};

export type GenqlCommentThreadLike = Pick<
  Genql.CommentThread,
  'id' | 'parentType' | 'parentId' | 'graphicId' | 'resolved' | 'resolvedAt'
> & {
  coordinates: Pick<Genql.CommentCoordinates, 'x' | 'y'>;
  comments: GenqlCommentLike[];
  systemAttributes: GenqlSystemAttributesLike;
};

function toUUID(id: string): UUID {
  assertIsUUID(id);
  return id;
}

function toOptionalUUID(id: string | null | undefined): UUID | undefined {
  return id ? toUUID(id) : undefined;
}

function toOptionalDate(value: string | null | undefined): Date | undefined {
  return value ? new Date(value) : undefined;
}

export function mapCommentUser(user: GenqlCommentUserLike): CommentUser {
  return {
    id: toUUID(user.id),
    username: user.username,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
  };
}

export function mapCommentSystemAttributes(
  attributes: GenqlSystemAttributesLike,
): CommentSystemAttributes {
  return {
    createdAt: new Date(attributes.createdAt),
    createdBy: attributes.createdBy
      ? mapCommentUser(attributes.createdBy)
      : undefined,
    updatedAt: new Date(attributes.updatedAt),
    updatedBy: attributes.updatedBy
      ? mapCommentUser(attributes.updatedBy)
      : undefined,
    hiddenAt: attributes.hiddenAt ? new Date(attributes.hiddenAt) : undefined,
    hiddenBy: attributes.hiddenBy
      ? mapCommentUser(attributes.hiddenBy)
      : undefined,
    isHidden: attributes.isHidden ?? undefined,
  };
}

export function mapCommentReaction(
  reaction: GenqlCommentReactionLike,
): CommentReaction {
  return {
    emoji: reaction.emoji,
    users: reaction.users.map(mapCommentUser),
  };
}

export function mapComment(comment: GenqlCommentLike): Comment {
  return {
    id: toUUID(comment.id),
    threadId: toUUID(comment.threadId),
    text: comment.text,
    author: mapCommentUser(comment.author),
    reactions: comment.reactions.map(mapCommentReaction),
    readBy: (comment.readBy ?? []).map((user) => toUUID(user.id)),
    systemAttributes: mapCommentSystemAttributes(comment.systemAttributes),
  };
}

export function mapCommentThread(
  thread: GenqlCommentThreadLike,
): CommentThread {
  return {
    id: toUUID(thread.id),
    parentType: thread.parentType,
    parentId: toUUID(thread.parentId),
    coordinates: { x: thread.coordinates.x, y: thread.coordinates.y },
    graphicId: thread.graphicId ? toUUID(thread.graphicId) : undefined,
    resolved: thread.resolved,
    resolvedAt: thread.resolvedAt ? new Date(thread.resolvedAt) : undefined,
    comments: thread.comments.map(mapComment),
    systemAttributes: mapCommentSystemAttributes(thread.systemAttributes),
  };
}

export function toGenqlCommentThreadSort(
  orderBy?: CommentThreadSort,
): Genql.CommentThreadSort | undefined {
  if (!orderBy) {
    return undefined;
  }
  const sort =
    orderBy.order === 'ASC' ? Genql.enumSort.ASC : Genql.enumSort.DESC;
  return orderBy.field === 'createdAt'
    ? { createdAt: sort }
    : { updatedAt: sort };
}

export type GenqlCommentEventPayload = Pick<
  Genql.Event,
  'event' | 'commentEvent' | '__typename'
>;

export function mapCommentCreatedPayload(
  payload: Genql.CommentCreatedPayload,
): CommentCreatedPayload {
  return {
    threadId: toUUID(payload.threadId),
    parentType: payload.parentType ?? undefined,
    parentId: toOptionalUUID(payload.parentId),
    coordinates: payload.coordinates
      ? { x: payload.coordinates.x, y: payload.coordinates.y }
      : undefined,
    graphicId: toOptionalUUID(payload.graphicId),
    resolved: payload.resolved ?? undefined,
    resolvedAt: toOptionalDate(payload.resolvedAt),
    threadSystemAttributes: payload.threadSystemAttributes
      ? mapCommentSystemAttributes(payload.threadSystemAttributes)
      : undefined,
    comment: mapComment(payload.comment),
  };
}

export function mapCommentUpdatedPayload(
  payload: Genql.CommentUpdatedPayload,
): CommentUpdatedPayload {
  return {
    threadId: toUUID(payload.threadId),
    commentId: toUUID(payload.commentId),
    text: payload.text ?? undefined,
    systemAttributes: payload.systemAttributes
      ? mapCommentSystemAttributes(payload.systemAttributes)
      : undefined,
  };
}

export function mapCommentsDeletedPayload(
  payload: Genql.CommentsDeletedPayload,
): CommentsDeletedPayload {
  return {
    threadId: toUUID(payload.threadId),
    commentIds: payload.commentIds.map(toUUID),
    deleteThread: payload.deleteThread,
  };
}

export function mapCommentThreadUpdatedPayload(
  payload: Genql.CommentThreadUpdatedPayload,
): CommentThreadUpdatedPayload {
  return {
    threadId: toUUID(payload.threadId),
    coordinates: payload.coordinates
      ? { x: payload.coordinates.x, y: payload.coordinates.y }
      : undefined,
    graphicId: toOptionalUUID(payload.graphicId),
    threadSystemAttributes: payload.threadSystemAttributes
      ? mapCommentSystemAttributes(payload.threadSystemAttributes)
      : undefined,
    commentId: toOptionalUUID(payload.commentId),
    text: payload.text ?? undefined,
    commentSystemAttributes: payload.commentSystemAttributes
      ? mapCommentSystemAttributes(payload.commentSystemAttributes)
      : undefined,
  };
}

export function mapCommentThreadResolvedPayload(
  payload: Genql.CommentThreadResolvedPayload,
): CommentThreadResolvedPayload {
  return {
    threadId: toUUID(payload.threadId),
    resolved: payload.resolved,
    resolvedAt: toOptionalDate(payload.resolvedAt),
    systemAttributes: payload.systemAttributes
      ? mapCommentSystemAttributes(payload.systemAttributes)
      : undefined,
  };
}

export function mapCommentThreadDeletedPayload(
  payload: Genql.CommentThreadDeletedPayload,
): CommentThreadDeletedPayload {
  return {
    threadId: toUUID(payload.threadId),
  };
}

export function mapCommentReactionPayload(
  payload: Genql.CommentReactionPayload,
): CommentReactionPayload {
  return {
    commentId: toUUID(payload.commentId),
    threadId: toUUID(payload.threadId),
    emoji: payload.emoji,
    user: mapCommentUser(payload.user),
  };
}

type GenqlCommentEvent = NonNullable<GenqlCommentEventPayload['commentEvent']>;
type CommentEventMapper = (
  commentEvent: GenqlCommentEvent,
) => CommentEvent | null;

function mapReactionChangedEvent(
  type: 'ADD_COMMENT_REACTION' | 'REMOVE_COMMENT_REACTION',
  commentEvent: GenqlCommentEvent,
): CommentEvent | null {
  return commentEvent.reactionChanged == null
    ? null
    : {
        type,
        payload: mapCommentReactionPayload(commentEvent.reactionChanged),
      };
}

const commentEventMappers: Record<CommentEvent['type'], CommentEventMapper> = {
  CREATE_COMMENT: (commentEvent) =>
    commentEvent.commentCreated == null
      ? null
      : {
          type: 'CREATE_COMMENT',
          payload: mapCommentCreatedPayload(commentEvent.commentCreated),
        },
  UPDATE_COMMENT: (commentEvent) =>
    commentEvent.commentUpdated == null
      ? null
      : {
          type: 'UPDATE_COMMENT',
          payload: mapCommentUpdatedPayload(commentEvent.commentUpdated),
        },
  DELETE_COMMENT: (commentEvent) =>
    commentEvent.commentsDeleted == null
      ? null
      : {
          type: 'DELETE_COMMENT',
          payload: mapCommentsDeletedPayload(commentEvent.commentsDeleted),
        },
  UPDATE_COMMENT_THREAD: (commentEvent) =>
    commentEvent.threadUpdated == null
      ? null
      : {
          type: 'UPDATE_COMMENT_THREAD',
          payload: mapCommentThreadUpdatedPayload(commentEvent.threadUpdated),
        },
  RESOLVE_COMMENT_THREAD: (commentEvent) =>
    commentEvent.threadResolved == null
      ? null
      : {
          type: 'RESOLVE_COMMENT_THREAD',
          payload: mapCommentThreadResolvedPayload(commentEvent.threadResolved),
        },
  DELETE_COMMENT_THREAD: (commentEvent) =>
    commentEvent.threadDeleted == null
      ? null
      : {
          type: 'DELETE_COMMENT_THREAD',
          payload: mapCommentThreadDeletedPayload(commentEvent.threadDeleted),
        },
  ADD_COMMENT_REACTION: (commentEvent) =>
    mapReactionChangedEvent('ADD_COMMENT_REACTION', commentEvent),
  REMOVE_COMMENT_REACTION: (commentEvent) =>
    mapReactionChangedEvent('REMOVE_COMMENT_REACTION', commentEvent),
};

function isCommentEventType(
  eventType: string,
): eventType is CommentEvent['type'] {
  return eventType in commentEventMappers;
}

export function mapGenqlCommentEventPayload(
  genqlEvent: GenqlCommentEventPayload,
): CommentEvent | null {
  const commentEvent = genqlEvent.commentEvent;
  if (commentEvent == null) {
    return null;
  }
  if (!isCommentEventType(genqlEvent.event)) {
    return null;
  }
  const eventMapper = commentEventMappers[genqlEvent.event];
  return eventMapper?.(commentEvent) ?? null;
}
